import React, { useEffect, useMemo, useState } from "react";
import { api, API_BASE } from "../lib/api";
import DashboardLayout from "../components/DashboardLayout";
import ProfileCheckins from "../components/ProfileCheckins";
import { toast } from "sonner";
import {
  Users, EnvelopeSimple, Phone, CurrencyDollar, Quotes, Calendar,
  Target, MagnifyingGlass, CaretRight, Headphones, PaperPlaneTilt, Crown,
} from "@phosphor-icons/react";

const fmt = (n) => "$" + Math.round(n || 0).toLocaleString();
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
};

export default function AdminProfiles() {
  const [roster, setRoster] = useState([]);
  const [search, setSearch] = useState("");
  const [activeEmail, setActiveEmail] = useState(null);
  const [profile, setProfile] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/users")
      .then((r) => {
        setRoster(r.data.users || []);
        if (r.data.users?.length) setActiveEmail(r.data.users[0].email);
      })
      .catch(() => toast.error("Failed to load roster"));
  }, []);

  // Load full profile when active email changes
  useEffect(() => {
    if (!activeEmail) return undefined;
    let alive = true;
    const run = async () => {
      setBusy(true);
      try {
        const r = await api.get(`/admin/profile/${encodeURIComponent(activeEmail)}`);
        if (alive) setProfile(r.data);
      } catch {
        if (alive) toast.error("Failed to load profile");
      } finally {
        if (alive) setBusy(false);
      }
    };
    run();
    return () => { alive = false; };
  }, [activeEmail]);

  const filtered = useMemo(() => {
    if (!search.trim()) return roster;
    const needle = search.toLowerCase();
    return roster.filter(
      (u) => (u.email || "").toLowerCase().includes(needle) ||
             (u.name || "").toLowerCase().includes(needle),
    );
  }, [search, roster]);

  const sendReactivate = async () => {
    if (!profile) return;
    if (!window.confirm(`Send a reactivation email to ${profile.identity.email}?`)) return;
    try {
      const r = await api.post(`/admin/users/${encodeURIComponent(profile.identity.email)}/reactivate`);
      toast.success(`Sent: ${r.data.subject}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    }
  };

  const downloadBrief = () => {
    if (!profile) return;
    const token = localStorage.getItem("exodus_token") || localStorage.getItem("ex_user_token") || "";
    const url = `${API_BASE}/admin/profile/${encodeURIComponent(profile.identity.email)}/brief.pdf`;
    // Use fetch with Authorization header → blob → download (so JWT travels)
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const blob = await r.blob();
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `precall-brief-${profile.identity.email.split("@")[0]}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success("Pre-call brief downloaded");
      })
      .catch((e) => toast.error(`PDF failed: ${e.message}`));
  };

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-10" data-testid="admin-profiles-page">
        <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
          <div>
            <p className="eyebrow"><span className="gold-divider" />Admin · Conversion Command Center</p>
            <h1 className="font-display text-4xl mt-3">Master User Profile</h1>
            <p className="text-sm text-exodus-muted mt-2 max-w-2xl">
              Every user, every quiz answer, every saved Goal Lab plan, every reservation, every
              audio episode in progress — in one view, pre-call ready, regardless of what they shared.
            </p>
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-6">
          {/* LEFT — roster */}
          <aside className="lg:col-span-4">
            <div className="card-surface flex items-center gap-3 px-4 py-2 mb-4">
              <MagnifyingGlass size={16} className="text-exodus-muted" />
              <input
                data-testid="profile-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name or email…"
                className="bg-transparent flex-1 text-sm focus:outline-none placeholder:text-exodus-muted/70"
              />
            </div>
            <div className="card-surface divide-y divide-exodus-border max-h-[75vh] overflow-auto">
              {filtered.length === 0 && (
                <p className="p-5 text-exodus-muted text-sm">No matches.</p>
              )}
              {filtered.map((u) => (
                <button
                  key={u.email}
                  data-testid={`roster-${u.email}`}
                  onClick={() => setActiveEmail(u.email)}
                  className={`w-full px-5 py-4 text-left transition-colors ${
                    activeEmail === u.email
                      ? "bg-exodus-elevated border-l-2 border-exodus-gold"
                      : "hover:bg-exodus-elevated"
                  }`}
                >
                  <p className="font-display text-base truncate">{u.name || u.email}</p>
                  <p className="text-xs text-exodus-muted truncate">{u.email}</p>
                  <div className="flex gap-2 mt-1 flex-wrap">
                    {u.quiz_count > 0 && (
                      <span className="text-[10px] uppercase tracking-wider gold-text">
                        Quiz · {u.quiz_count}
                      </span>
                    )}
                    {u.reservation_paid && (
                      <span className="text-[10px] uppercase tracking-wider text-emerald-400">PAID</span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </aside>

          {/* RIGHT — full profile */}
          <section className="lg:col-span-8 space-y-5">
            {busy && <p className="text-exodus-muted">Loading profile…</p>}
            {!busy && profile && (
              <>
                {/* Identity */}
                <div className="card-surface p-6" data-testid="profile-identity">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <p className="eyebrow text-xs">Identity</p>
                      <h2 className="font-display text-3xl mt-1">{profile.identity.name || "—"}</h2>
                      <div className="flex flex-wrap gap-3 mt-3 text-sm text-exodus-muted">
                        <span><EnvelopeSimple size={12} className="inline mr-1" />{profile.identity.email}</span>
                        {profile.identity.phone && (
                          <span><Phone size={12} className="inline mr-1" />{profile.identity.phone}</span>
                        )}
                        {profile.identity.stage && (
                          <span className="gold-text uppercase tracking-wider text-[10px]">
                            stage · {profile.identity.stage}
                          </span>
                        )}
                        {profile.identity.source && (
                          <span className="uppercase tracking-wider text-[10px]">
                            src · {profile.identity.source}
                          </span>
                        )}
                      </div>
                      {profile.identity.quiz_recommendation && (
                        <div className="mt-4 inline-block border border-exodus-gold/60 bg-exodus-gold/10 px-4 py-2">
                          <p className="text-[10px] eyebrow text-exodus-gold">Quiz pointed here</p>
                          <p className="font-display text-lg gold-text">
                            {profile.identity.quiz_recommendation.primary_label}
                          </p>
                        </div>
                      )}
                      {profile.identity.notes && (
                        <p className="text-xs text-exodus-muted mt-4 italic max-w-xl">
                          Internal note: {profile.identity.notes}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0 flex-wrap">
                      <button
                        data-testid="profile-brief-pdf"
                        onClick={downloadBrief}
                        className="btn-gold text-xs flex items-center gap-1"
                      >
                        <PaperPlaneTilt size={14} weight="bold" /> Pre-Call Brief PDF
                      </button>
                      <button
                        data-testid="profile-reactivate"
                        onClick={sendReactivate}
                        className="btn-ghost text-xs flex items-center gap-1"
                      >
                        <PaperPlaneTilt size={14} weight="bold" /> Reactivate
                      </button>
                    </div>
                  </div>
                </div>

                <ProfileCheckins email={profile.identity.email} />

                {/* KPI strip */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <K icon={Quotes} label="Quizzes taken" value={profile.summary.quiz_count} />
                  <K icon={Target} label="Saved scenarios" value={profile.summary.scenario_count} />
                  <K icon={Calendar} label="Reservations (paid)" value={`${profile.summary.paid_count}/${profile.summary.reservation_count}`} />
                  <K icon={CurrencyDollar} label="Revenue" value={fmt(profile.summary.revenue_usd)} />
                </div>

                {/* Quizzes */}
                {profile.quizzes?.length > 0 && (
                  <Card icon={Quotes} title="Assessment history">
                    {profile.quizzes.map((q, i) => (
                      <div key={q.id || i} className="border-l-2 border-exodus-gold/40 pl-3 py-2 text-sm">
                        <p className="text-xs text-exodus-muted">{fmtDate(q.created_at)}</p>
                        <p className="font-display text-base">
                          Primary: <span className="gold-text">
                            {(q.recommendation || {}).primary_label || "—"}
                          </span>
                        </p>
                        {q.answers && (
                          <details className="mt-2">
                            <summary className="text-[10px] eyebrow cursor-pointer text-exodus-muted">
                              Show all answers
                            </summary>
                            <pre className="text-[10px] text-exodus-muted mt-2 overflow-auto whitespace-pre-wrap font-mono">
                              {JSON.stringify(q.answers, null, 2)}
                            </pre>
                          </details>
                        )}
                      </div>
                    ))}
                  </Card>
                )}

                {/* Scenarios */}
                {profile.scenarios?.length > 0 && (
                  <Card icon={Target} title={`Goal Lab plans (${profile.scenarios.length})`}>
                    <div className="space-y-3">
                      {profile.scenarios.map((s) => (
                        <div key={s.id} className="border border-exodus-border p-3 bg-exodus-elevated/40">
                          <div className="flex items-start justify-between gap-2 flex-wrap">
                            <div>
                              <p className="text-[10px] eyebrow text-exodus-muted">
                                {s.goal_type.replace(/_/g, " ")}
                              </p>
                              <p className="font-display text-base mt-1">{s.label}</p>
                            </div>
                            <span className="text-[10px] gold-text uppercase font-mono">{s.product}</span>
                          </div>
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3 text-xs">
                            <Mini label="Age" v={`${s.current_age}→${s.target_age}`} />
                            <Mini label="Monthly" v={fmt(s.monthly_contribution)} />
                            <Mini label="Lump" v={fmt(s.lump_sum)} />
                            <Mini label="Target" v={fmt(s.target_amount)} />
                          </div>
                          <div className="mt-3 pt-3 border-t border-exodus-border grid grid-cols-2 gap-3 text-xs">
                            <Mini label="Projected" v={fmt((s.result || {}).projected_value)} gold />
                            <Mini label="Annual income"
                                  v={fmt(((s.result || {}).income_phase || {}).annual_tax_free_income
                                       || ((s.result || {}).income_phase || {}).annual_lifetime_income || 0)}
                                  gold />
                          </div>
                        </div>
                      ))}
                    </div>
                  </Card>
                )}

                {/* Reservations */}
                {profile.reservations?.length > 0 && (
                  <Card icon={Calendar} title="Reservations & checkouts">
                    {profile.reservations.map((r, i) => (
                      <div key={r.session_id || i} className="border-l-2 border-exodus-border pl-3 py-2 text-sm">
                        <p className="text-xs text-exodus-muted">{fmtDate(r.created_at)}</p>
                        <p className="font-display text-base">
                          {fmt(r.amount)} ·{" "}
                          <span className={
                            r.payment_status === "paid"
                              ? "text-emerald-400"
                              : "text-amber-400"
                          }>
                            {r.payment_status || "pending"}
                          </span>
                        </p>
                      </div>
                    ))}
                  </Card>
                )}

                {/* Listening progress */}
                {profile.listening_progress?.length > 0 && (
                  <Card icon={Headphones} title="Audio they're consuming">
                    {profile.listening_progress.map((lp, i) => (
                      <div key={i} className="border-l-2 border-exodus-border pl-3 py-2">
                        <p className="text-xs text-exodus-muted">{fmtDate(lp.updated_at)}</p>
                        <p className="text-sm">{lp.series_id} · Ep {lp.episode_number}</p>
                        <div className="mt-1 h-1 bg-exodus-border rounded">
                          <div className="h-1 bg-exodus-gold rounded"
                               style={{ width: `${Math.round((lp.percent || 0) * 100)}%` }} />
                        </div>
                      </div>
                    ))}
                  </Card>
                )}

                {/* Reactivations */}
                {profile.reactivations?.length > 0 && (
                  <Card icon={PaperPlaneTilt} title="Reactivation log">
                    {profile.reactivations.map((r, i) => (
                      <div key={r.id || i} className="border-l-2 border-exodus-border pl-3 py-2 text-sm">
                        <p className="text-xs text-exodus-muted">{fmtDate(r.created_at)}</p>
                        <p className="font-display text-base">{r.subject}</p>
                      </div>
                    ))}
                  </Card>
                )}
              </>
            )}
          </section>
        </div>
      </div>
    </DashboardLayout>
  );
}

function K({ icon: Icon, label, value }) {
  return (
    <div className="card-surface p-4 flex items-center gap-3">
      <Icon size={18} weight="thin" className="text-exodus-gold shrink-0" />
      <div className="min-w-0">
        <p className="text-[10px] eyebrow text-exodus-muted">{label}</p>
        <p className="font-display text-xl truncate">{value}</p>
      </div>
    </div>
  );
}

function Card({ icon: Icon, title, children }) {
  return (
    <div className="card-surface p-6">
      <div className="flex items-center gap-2 mb-3">
        <Icon size={16} className="text-exodus-gold" weight="thin" />
        <p className="eyebrow text-xs">{title}</p>
      </div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function Mini({ label, v, gold }) {
  return (
    <div>
      <p className="text-[9px] eyebrow text-exodus-muted">{label}</p>
      <p className={`text-sm font-display mt-1 ${gold ? "gold-text" : ""}`}>{v}</p>
    </div>
  );
}
